import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Tabs, Typography, Space, Divider, Button, Form, Input, Alert, Steps, message } from 'antd';
import { LoginOutlined, UserAddOutlined, CalendarOutlined, MailOutlined, PhoneOutlined, LockOutlined, SearchOutlined } from '@ant-design/icons';
import PublicLayout from '../components/landing/PublicLayout';
import CustomerLogin from '../components/customer/CustomerLogin';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { getThemeColors } from '../config/shop.config';
import { customerPortalApi } from '../services/apiInstances';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const CustomerLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, register } = useCustomerAuth();
  const [activeTab, setActiveTab] = useState('login');
  const themeColors = getThemeColors();

  // Registration state
  const [registerStep, setRegisterStep] = useState(0); // 0 = find customer, 1 = create account
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [matchedCustomers, setMatchedCustomers] = useState<any[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [identifierForm] = Form.useForm();
  const [registerForm] = Form.useForm();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/customer/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleLoginSuccess = () => {
    navigate('/customer/dashboard');
  };

  const handleBookAppointment = () => {
    navigate('/book');
  };

  // Step 1: Look up existing customer by email or phone
  const handleLookup = async (values: { identifier: string }) => {
    setRegisterLoading(true);
    setRegisterError(null);
    try {
      const response = await customerPortalApi.post('/customer-auth/check-identifier', {
        identifier: values.identifier,
      });

      // ── Why a match STOPS here ────────────────────────────────────────────
      //
      // Claiming an existing service record requires the link the shop itself
      // issued for it (WP-04 I-1: register refuses a `customerId` without a
      // matching `portalToken`). This screen has no such link, so it used to send
      // the id anyway and every single attempt came back
      // `403 REGISTRATION_NOT_AUTHORIZED` — a create-account form that could not
      // create an account. Verified against the running API on 2026-07-30.
      //
      // So: say what will work instead of failing. A customer with records gets
      // told to use their estimate/invoice link, which is also where the account
      // offer now lives.
      if (response.data.hasExistingCustomers) {
        setMatchedCustomers(response.data.customers);
        setRegisterStep(0);
        setRegisterError(
          'We already have service records for that email or phone. To link them to a new ' +
            'login, open the estimate or invoice link we sent you — there is a "Create an ' +
            'account" option on it. That link is how we confirm the records are yours. ' +
            'If you cannot find it, call the shop and we will resend it.'
        );
        return;
      }

      // Nothing on file: a genuine self-registration, which the API allows with no
      // customer id and no token. Pre-fill what they already typed.
      setMatchedCustomers([]);
      setSelectedCustomerId(null);
      setRegisterStep(1);
      const isEmail = values.identifier.includes('@');
      if (isEmail) {
        registerForm.setFieldsValue({ email: values.identifier });
      } else {
        registerForm.setFieldsValue({ phone: values.identifier });
      }
    } catch (error: any) {
      setRegisterError(error.response?.data?.error || 'Failed to look up customer');
    } finally {
      setRegisterLoading(false);
    }
  };

  // Step 2: Register account
  const handleRegister = async (values: { email?: string; phone?: string; password: string; confirmPassword: string; referralCode?: string }) => {
    if (values.password !== values.confirmPassword) {
      setRegisterError('Passwords do not match');
      return;
    }
    setRegisterLoading(true);
    setRegisterError(null);
    try {
      // No `customerId`: this is a self-registration. Sending one without the
      // shop's portal token is refused outright — see handleLookup.
      const { signedIn } = await register({
        email: values.email || undefined,
        phone: values.phone || undefined,
        password: values.password,
        referralCode: values.referralCode || undefined,
      });

      // Self-registration deliberately returns no session, so claiming to be
      // logged in and navigating to the dashboard produced a page that 401'd on
      // every request. Send them to the login tab with the identifier filled in.
      if (signedIn) {
        message.success('Account created! Redirecting to dashboard...');
        navigate('/customer/dashboard');
        return;
      }
      // Not prefilled into the Login tab on purpose: `CustomerLogin` DISABLES the
      // identifier input when it is given one, so a typo would be unfixable.
      message.success(
        `Account created. Please sign in with ${values.email || values.phone}.`,
        6
      );
      resetRegistration();
      setActiveTab('login');
    } catch (error: any) {
      setRegisterError(error.message || 'Registration failed');
    } finally {
      setRegisterLoading(false);
    }
  };

  const resetRegistration = () => {
    setRegisterStep(0);
    setMatchedCustomers([]);
    setSelectedCustomerId(null);
    setRegisterError(null);
    identifierForm.resetFields();
    registerForm.resetFields();
  };

  return (
    <PublicLayout>
      <div style={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ maxWidth: 500, width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <Title level={2} style={{ color: 'white', marginBottom: 8 }}>
              Customer Portal
            </Title>
            <Paragraph style={{ color: 'rgba(255,255,255,0.9)', fontSize: 16 }}>
              Track your vehicle service history and appointments
            </Paragraph>
          </div>

          <Card>
            <Tabs activeKey={activeTab} onChange={(key) => { setActiveTab(key); resetRegistration(); }} centered>
              <TabPane
                tab={
                  <span>
                    <LoginOutlined />
                    Login
                  </span>
                }
                key="login"
              >
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <CustomerLogin onSuccess={handleLoginSuccess} />

                  <Divider plain>or</Divider>

                  <Card
                    style={{
                      background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.accent} 100%)`,
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                    }}
                    hoverable
                    onClick={handleBookAppointment}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <CalendarOutlined style={{ fontSize: 32, color: 'white', marginBottom: 8 }} />
                      <Title level={4} style={{ color: 'white', margin: '8px 0 4px 0' }}>
                        Book an Appointment
                      </Title>
                      <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                        New customer? Start by booking a service
                      </Text>
                    </div>
                  </Card>
                </Space>
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <UserAddOutlined />
                    Create Account
                  </span>
                }
                key="register"
              >
                <Space direction="vertical" size="middle" style={{ width: '100%', padding: '12px 0' }}>
                  <Steps
                    current={registerStep}
                    size="small"
                    items={[
                      { title: 'Find Your Profile' },
                      { title: 'Set Password' },
                    ]}
                    style={{ marginBottom: 8 }}
                  />

                  {registerError && (
                    <Alert
                      message={registerError}
                      type="error"
                      showIcon
                      closable
                      onClose={() => setRegisterError(null)}
                    />
                  )}

                  {registerStep === 0 && (
                    <>
                      <div style={{ textAlign: 'center' }}>
                        <Title level={4} style={{ marginBottom: 4 }}>Find Your Customer Profile</Title>
                        <Text type="secondary">
                          Enter the email or phone number you used when visiting our shop
                        </Text>
                      </div>

                      <Form form={identifierForm} layout="vertical" onFinish={handleLookup}>
                        <Form.Item
                          name="identifier"
                          label="Email or Phone Number"
                          rules={[{ required: true, message: 'Enter your email or phone number' }]}
                        >
                          <Input
                            prefix={<SearchOutlined />}
                            placeholder="email@example.com or (555) 123-4567"
                            size="large"
                          />
                        </Form.Item>
                        <Form.Item>
                          <Button type="primary" htmlType="submit" size="large" block loading={registerLoading}>
                            Look Up My Profile
                          </Button>
                        </Form.Item>
                      </Form>

                      <Divider plain>Don't have a profile yet?</Divider>

                      <Card
                        size="small"
                        style={{
                          background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.accent} 100%)`,
                          border: 'none',
                          cursor: 'pointer',
                        }}
                        hoverable
                        onClick={handleBookAppointment}
                      >
                        <div style={{ textAlign: 'center' }}>
                          <CalendarOutlined style={{ fontSize: 24, color: 'white', marginBottom: 4 }} />
                          <Title level={5} style={{ color: 'white', margin: '4px 0' }}>
                            Book Your First Appointment
                          </Title>
                          <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13 }}>
                            We'll create your profile during booking
                          </Text>
                        </div>
                      </Card>
                    </>
                  )}

                  {registerStep === 1 && (
                    <>
                      <div style={{ textAlign: 'center' }}>
                        <Title level={4} style={{ marginBottom: 4 }}>Create Your Account</Title>
                        <Text type="secondary">Set up a password to access your customer portal</Text>
                      </div>

                      {/* Customer selection */}
                      {matchedCustomers.length > 1 && (
                        <Card size="small" style={{ background: '#f0f5ff', border: '1px solid #adc6ff' }}>
                          <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            Multiple profiles found. Select yours:
                          </Text>
                          <Space direction="vertical" style={{ width: '100%' }}>
                            {matchedCustomers.map((c) => (
                              <Card
                                key={c.id}
                                size="small"
                                hoverable
                                onClick={() => setSelectedCustomerId(c.id)}
                                style={{
                                  border: selectedCustomerId === c.id ? `2px solid ${themeColors.primary}` : '1px solid #d9d9d9',
                                  background: selectedCustomerId === c.id ? '#e6f7ff' : 'white',
                                }}
                              >
                                <Text strong>{c.name}</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {c.vehicleCount} vehicle{c.vehicleCount !== 1 ? 's' : ''} on file
                                  {c.lastServiceDate && !isNaN(new Date(c.lastServiceDate).getTime())
                                    ? ` · Last visit: ${new Date(c.lastServiceDate).toLocaleDateString()}`
                                    : ''}
                                </Text>
                              </Card>
                            ))}
                          </Space>
                        </Card>
                      )}

                      {matchedCustomers.length === 1 && (
                        <Card size="small" style={{ background: '#f6ffed', border: '1px solid #b7eb8f' }}>
                          <Text strong style={{ color: '#52c41a' }}>Profile found: </Text>
                          <Text strong>{matchedCustomers[0].name}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {matchedCustomers[0].vehicleCount} vehicle{matchedCustomers[0].vehicleCount !== 1 ? 's' : ''} on file
                          </Text>
                        </Card>
                      )}

                      <Form form={registerForm} layout="vertical" onFinish={handleRegister}>
                        <Form.Item
                          name="email"
                          label="Email"
                          dependencies={['phone']}
                          rules={[
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (value || getFieldValue('phone')) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(
                                  new Error('Enter an email or phone number')
                                );
                              },
                            }),
                          ]}
                          extra="Provide at least one: email or phone"
                        >
                          <Input prefix={<MailOutlined />} placeholder="email@example.com" size="large" />
                        </Form.Item>

                        <Form.Item
                          name="phone"
                          label="Phone"
                          dependencies={['email']}
                          rules={[
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (value || getFieldValue('email')) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(
                                  new Error('Enter an email or phone number')
                                );
                              },
                            }),
                          ]}
                        >
                          <Input prefix={<PhoneOutlined />} placeholder="(555) 123-4567" size="large" />
                        </Form.Item>

                        <Form.Item
                          name="password"
                          label="Create Password"
                          rules={[
                            { required: true, message: 'Please create a password' },
                            { min: 8, message: 'Password must be at least 8 characters' },
                          ]}
                        >
                          <Input.Password prefix={<LockOutlined />} placeholder="At least 8 characters" size="large" />
                        </Form.Item>

                        <Form.Item
                          name="confirmPassword"
                          label="Confirm Password"
                          rules={[
                            { required: true, message: 'Please confirm your password' },
                          ]}
                        >
                          <Input.Password prefix={<LockOutlined />} placeholder="Re-enter password" size="large" />
                        </Form.Item>

                        <Form.Item name="referralCode" label="Referral Code (optional)">
                          <Input placeholder="Enter referral code if you have one" size="large" />
                        </Form.Item>

                        <Form.Item>
                          <Space direction="vertical" style={{ width: '100%' }} size="small">
                            <Button
                              type="primary"
                              htmlType="submit"
                              size="large"
                              block
                              loading={registerLoading}
                              disabled={!selectedCustomerId}
                            >
                              Create Account
                            </Button>
                            <Button type="link" block onClick={resetRegistration}>
                              Start Over
                            </Button>
                          </Space>
                        </Form.Item>
                      </Form>
                    </>
                  )}
                </Space>
              </TabPane>
            </Tabs>
          </Card>
        </div>
      </div>
    </PublicLayout>
  );
};

export default CustomerLoginPage;
